/**
 * The Directional & MWD gate for the well-job screens (api#1055).
 *
 * Every /downhole/jobs door answers 409 DD_MODE_OFF while the company's
 * dd_mwd mode is off, so these screens ask nothing until the switch is read.
 * Four outcomes, never collapsed: still reading (a spinner, not "off"), the
 * read failed (the load-failure notice), off (a sentence saying so, with the
 * way to turn it on for an admin), on (the screen).
 *
 * The sidebar hides Wells while the mode is off, but a bookmark still lands
 * here, which is why the off state is a sentence and not a redirect.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { LoadFailureNotice } from '../../components/ui/LoadFailureNotice';
import { useCanWriteCompanySettings } from '../../hooks/useCanWriteCompanySettings';
import { useDownholeSettings } from '../../hooks/useDownholeSettings';
import { queryFailureError, queryLoadFailure } from '../../lib/load-failure';

export function DdModeGate({ children }: { children: React.ReactNode }) {
  const query = useDownholeSettings();
  const canTurnOn = useCanWriteCompanySettings();
  const failure = queryLoadFailure(query);

  if (failure) {
    return (
      <div className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
        <LoadFailureNotice error={queryFailureError(query)} kind={failure} subject="downhole settings" />
      </div>
    );
  }
  if (query.data?.data?.dd_mwd_enabled === true) return <>{children}</>;
  if (query.isLoading || !query.data) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="size-6 animate-spin text-fieldiq-muted" aria-label="Loading downhole settings" />
      </div>
    );
  }
  return (
    <div data-testid="dd-mode-off" className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)]">
      <div className="mx-auto max-w-[1920px] rounded-fi-panel border border-fieldiq-border bg-fieldiq-card px-4 py-10 text-center">
        <p className="text-sm text-fieldiq-text">Directional &amp; MWD is turned off for this company, so there are no well jobs to show.</p>
        {canTurnOn ? (
          <Link to="/settings/downhole" className="mt-3 inline-block text-sm font-bold text-fieldiq-accent-signal underline">
            Turn it on in Downhole settings
          </Link>
        ) : (
          <p className="mt-2 text-xs text-fieldiq-muted">An admin can turn it on in Downhole settings.</p>
        )}
      </div>
    </div>
  );
}
